import React from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, Tag, ShoppingBag, Users, Wallet, Activity, ArrowLeft, LogOut, Copy, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const allItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, tid: "admin-nav-dashboard", roles: ["admin", "sub_admin"] },
  { to: "/admin/products", icon: Package, label: "Produk", tid: "admin-nav-products", roles: ["admin", "sub_admin"] },
  { to: "/admin/categories", icon: Tag, label: "Kategori", tid: "admin-nav-categories", roles: ["admin", "sub_admin"] },
  { to: "/admin/orders", icon: ShoppingBag, label: "Order", tid: "admin-nav-orders", roles: ["admin", "sub_admin"] },
  { to: "/admin/deposits", icon: Wallet, label: "Top Up", tid: "admin-nav-deposits", roles: ["admin", "sub_admin"] },
  { to: "/admin/withdrawals", icon: Wallet, label: "Tarik", tid: "admin-nav-withdrawals", roles: ["admin", "sub_admin"] },
  { to: "/admin/users", icon: Users, label: "User", tid: "admin-nav-users", roles: ["admin", "sub_admin"] },
  { to: "/admin/sub-admins", icon: Users, label: "Kelola Admin", tid: "admin-nav-subadmins", roles: ["admin"] },
  { to: "/admin/master-panel", icon: Activity, label: "Master Panel", tid: "admin-nav-master", roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const copyRefLink = () => {
    const link = `${window.location.origin}/register?ref=${user.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Link referral disalin!");
  };

  const copyRefCode = () => {
    navigator.clipboard.writeText(user.referral_code);
    toast.success("Kode referral disalin!");
  };

  if (!user || user === false || !["admin", "sub_admin"].includes(user.role)) {
    return (
      <div className="min-h-screen grid place-items-center font-mono">
        <div className="border border-black p-8 shadow-brutal max-w-sm text-center">
          <div className="label-mono text-[#FF3333]">// 403 FORBIDDEN</div>
          <h2 className="font-display text-2xl font-black uppercase mt-2">Akses ditolak</h2>
          <p className="text-sm text-zinc-600 mt-2">Halaman ini hanya untuk admin.</p>
          <Link to="/" className="btn-primary mt-4 inline-block">Kembali</Link>
        </div>
      </div>
    );
  }

  const items = allItems.filter((it) => it.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-60 hidden lg:flex flex-col border-r border-black bg-white">
        <div className="h-16 px-5 border-b border-black flex items-center gap-2">
          <div className="w-7 h-7 bg-black text-white grid place-items-center font-mono font-black text-sm">A</div>
          <span className="font-display font-black uppercase tracking-tight">AleShop/Master</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 font-mono text-xs uppercase font-bold tracking-widest border ${
                  isActive ? "bg-black text-white border-black" : "border-transparent hover:border-black hover:bg-zinc-100"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* LINK & KODE REFERRAL */}
        <div className="mx-3 mb-2 border border-black bg-zinc-50">
          <div className="px-3 py-2 border-b border-black flex items-center gap-1">
            <LinkIcon className="w-3 h-3 text-zinc-500" />
            <div className="label-mono text-[10px] text-zinc-500">LINK & KODE REFERRAL</div>
          </div>
          <div className="p-2 space-y-2">
            <div>
              <div className="font-mono text-[9px] text-zinc-400 mb-1">KODE</div>
              <div className="flex items-center gap-1">
                <div className="flex-1 bg-[#FFD600] px-2 py-1.5 font-mono text-xs font-black tracking-widest border border-black truncate">
                  {user.referral_code}
                </div>
                <button onClick={copyRefCode} className="p-1.5 border border-black hover:bg-black hover:text-white flex-shrink-0" title="Salin kode">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div>
              <div className="font-mono text-[9px] text-zinc-400 mb-1">LINK DAFTAR</div>
              <div className="flex items-center gap-1">
                <div className="flex-1 bg-white px-2 py-1.5 font-mono text-[9px] border border-black truncate text-zinc-500">
                  aleshopnow.store/register?ref={user.referral_code}
                </div>
                <button onClick={copyRefLink} className="p-1.5 border border-black hover:bg-black hover:text-white flex-shrink-0" title="Salin link">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="m-3 mt-0 space-y-2">
          {user.role === "sub_admin" && (
            <div className="px-3 py-2 border border-black bg-[#FFD600] font-mono text-[10px] uppercase font-bold tracking-widest text-center">
              Sub Admin
            </div>
          )}
          <Link to="/" className="px-3 py-2 border border-black font-mono text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:bg-black hover:text-white" data-testid="back-to-shop">
            <ArrowLeft className="w-3.5 h-3.5" /> Storefront
          </Link>
          <button onClick={handleLogout} className="w-full px-3 py-2 border border-black bg-[#FF3333] text-white font-mono text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:bg-black">
            <LogOut className="w-3.5 h-3.5" /> Keluar
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-black bg-white px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="label-mono text-[#FF6A00]">// MASTER PANEL</div>
            <span className="hidden md:block font-mono text-xs text-zinc-500">connected · v1.4.2</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex badge-status bg-[#00C853] text-white border-black">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-blink" /> ONLINE
            </span>
            <span className="font-mono text-xs">{user.name}</span>
            <button onClick={handleLogout} className="p-2 border border-black hover:bg-[#FF3333] hover:text-white" title="Keluar">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}